import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, Zap, ArrowRight, Sparkles } from 'lucide-react';

/* Animated iPhone mockup: someone asks ChatGPT for restaurants and YOUR
   restaurant (powered by Kitxens) wins the #1 spot. Loops forever. */

const TXT = {
  en: {
    query: 'best tacos near me',
    you: 'Your Restaurant',
    youSub: 'AI-optimized · Direct ordering',
    kitxens: 'Powered by Kitxens',
    comp1: 'Taco Bros', comp2: 'El Camion Grill',
    compSub: 'Menu in PDF · Not AI-readable',
    answer: 'Top spots near you:',
    banner: 'This could be your restaurant',
    cta: 'See my Score — free',
  },
  es: {
    query: 'mejores tacos cerca de mí',
    you: 'Tu Restaurante',
    youSub: 'Optimizado con IA · Pedidos directos',
    kitxens: 'Powered by Kitxens',
    comp1: 'Taco Bros', comp2: 'El Camion Grill',
    compSub: 'Menú en PDF · Ilegible para la IA',
    answer: 'Los mejores cerca de ti:',
    banner: 'Este podría ser tu restaurante',
    cta: 'Ver mi Score — gratis',
  },
};

export default function PhoneSimulation({ lang = 'en', compact = false, withCta = true, className = '' }) {
  const t = TXT[lang === 'es' ? 'es' : 'en'];
  const [phase, setPhase] = useState(0); // 0 typing · 1 thinking · 2 results · 3 highlight
  const [typed, setTyped] = useState('');

  useEffect(() => {
    let timers = [];
    if (phase === 0) {
      setTyped('');
      t.query.split('').forEach((_, i) => {
        timers.push(setTimeout(() => setTyped(t.query.slice(0, i + 1)), 250 + i * 55));
      });
      timers.push(setTimeout(() => setPhase(1), 250 + t.query.length * 55 + 500));
    } else if (phase === 1) {
      timers.push(setTimeout(() => setPhase(2), 1300));
    } else if (phase === 2) {
      timers.push(setTimeout(() => setPhase(3), 1400));
    } else {
      timers.push(setTimeout(() => setPhase(0), 4200));
    }
    return () => timers.forEach(clearTimeout);
  }, [phase, t.query]);

  const w = compact ? 'w-[200px]' : 'w-[250px] sm:w-[270px]';

  return (
    <motion.div
      animate={{ y: [0, -9, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      className={`${w} ${className}`}
      data-testid="phone-simulation"
    >
      <div className="relative rounded-[38px] border-[7px] border-neutral-900 bg-gradient-to-b from-[#FFF4E8] via-white to-[#FFF8F1] shadow-[0_45px_90px_-28px_rgba(216,101,26,0.38)] overflow-hidden">
        {/* notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-4.5 h-[17px] rounded-full bg-neutral-900 z-20" />
        {/* screen */}
        <div className={`pt-8 pb-4 px-3 ${compact ? 'min-h-[360px]' : 'min-h-[420px]'} flex flex-col`}>
          {/* app header */}
          <div className="flex items-center justify-center gap-1.5 pb-2 border-b border-orange-100">
            <Sparkles className="w-3 h-3 text-[#F77F11]" />
            <span className="text-[11px] font-semibold text-neutral-800">ChatGPT</span>
          </div>

          <div className="flex-1 pt-3 space-y-2.5">
            {/* user bubble */}
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#FFE3CC] px-3 py-2">
                <p className="text-[11px] text-neutral-900 leading-snug">
                  {typed}
                  {phase === 0 && <span className="inline-block w-[1.5px] h-3 bg-neutral-700 ml-px animate-pulse align-middle" />}
                </p>
              </div>
            </div>

            {/* thinking dots */}
            <AnimatePresence>
              {phase === 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex gap-1 pl-1 pt-1">
                  {[0, 1, 2].map((i) => (
                    <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-[#F5A96B]"
                      animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.2 }} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* results */}
            <AnimatePresence>
              {phase >= 2 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-1.5">
                  <p className="text-[10px] text-[#b4642f] pl-1">{t.answer}</p>
                  {/* winner card */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                    className={`rounded-xl p-2.5 border transition-all duration-500 ${phase === 3 ? 'border-[#F77F11] bg-orange-50 shadow-[0_0_24px_-6px_rgba(247,127,17,0.5)]' : 'border-neutral-200 bg-white'}`}
                    data-testid="phone-sim-winner"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold text-[#d8651a]">1.</span>
                      <p className="text-[11px] font-bold text-neutral-900 flex-1 truncate">{t.you}</p>
                      <span className="flex items-center gap-0.5 text-[9px] font-bold text-amber-500"><Star className="w-2.5 h-2.5 fill-amber-500" /> 4.8</span>
                    </div>
                    <p className="text-[8.5px] text-neutral-500 mt-0.5 pl-4">{t.youSub}</p>
                    <AnimatePresence>
                      {phase === 3 && (
                        <motion.span initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
                          className="mt-1.5 ml-4 inline-flex items-center gap-1 rounded-full bg-[#F77F11] px-2 py-0.5 text-[8px] font-bold text-white">
                          <Zap className="w-2 h-2" /> {t.kitxens}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                  {/* losers */}
                  {[t.comp1, t.comp2].map((n, i) => (
                    <motion.div key={n} initial={{ opacity: 0, y: 8 }} animate={{ opacity: phase === 3 ? 0.55 : 0.9, y: 0 }}
                      transition={{ delay: 0.35 + i * 0.2 }}
                      className="rounded-xl p-2.5 border border-orange-100/80 bg-white/70">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-neutral-400">{i + 2}.</span>
                        <p className="text-[11px] font-semibold text-neutral-600 flex-1 truncate">{n}</p>
                        <span className="flex items-center gap-0.5 text-[9px] text-neutral-400"><Star className="w-2.5 h-2.5" /> 4.{2 - i}</span>
                      </div>
                      <p className="text-[8.5px] text-neutral-400 mt-0.5 pl-4">{t.compSub}</p>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* banner */}
          <AnimatePresence>
            {phase === 3 && (
              <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="pt-2 text-center text-[9px] font-bold text-[#d8651a]">
                ↑ {t.banner}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>

      {withCta && (
        <Link to="/score" data-testid="phone-sim-cta"
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white h-10 text-[12.5px] font-bold shadow-lg shadow-orange-500/25 transition-colors">
          {t.cta} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </motion.div>
  );
}
